// What a visitor does with a game from one of the Studio's own pages: get a
// package, get its source, see every version, remix it. Reading a game's page,
// playing its browser version and reading the prompt that remixes it ask for
// nothing. A package, the source the Studio holds and the link code a remix
// is published with ask for an account, and the visitor makes or opens one
// here, in a dialog over the page they are on, and what they reached for goes
// on. A past version asks for the membership, offered the same way.
//
// The landing page's shelf and a game's page both use this (src/gate/main.ts).
// The play page's dock is on the game's own host, which knows no account: it
// draws the same panel and sends the visitor to the game's page for the rest
// (src/play/dock.ts).

import { POCKET3D_SOURCE, REMIX_GUIDE, remixPrompt } from "../core/agents.ts";
import type { EventName } from "../core/events.ts";
import { icon } from "../core/icons.ts";
import { formatPrice } from "../core/i18n.ts";
import { PRIVACY_PATH, TERMS_PATH, LEGAL } from "../core/legal.ts";
import { downloadHtml, sourceHtml, versionsHtml } from "../core/offer.ts";
import { PERKS, perkSvg, perkText, soonText } from "../core/perks.ts";
import type { CreateLinkResponse, GameFacts, GateResponse } from "../core/protocol.ts";
import { mainlandZone } from "../core/seller.ts";
import { NATIVE_TARGETS, type NativeTarget } from "../core/types.ts";
import { track } from "../api/events.ts";
import { h } from "../ui/dom.ts";
import { lang, localized, t } from "../ui/lang.ts";
import { rich } from "../ui/names.ts";
import { createSignIn } from "../ui/signin.ts";

/** What the address carries while a visitor is away signing in or paying: what they reached for. */
const WISH = ["get", "version", "remix", "link", "versions", "join", "game"] as const;
/** Where the page keeps what to open once a payment is confirmed. */
const AFTER_PAYMENT = "pocket.gate.after";
/** Where the tab keeps what a sign-in that left for Google or GitHub was for. */
const SIGNIN_LEFT = "pocket.signin.left";

const kept = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const keep = (key: string, value: string | null): void => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // A tab that keeps nothing says each thing as it happens.
  }
};

type Plan = "month" | "year";
/** What a package's tile hands over: a device's package, or the source the Studio holds. */
type Wanted = NativeTarget | "source";

export interface GateOptions {
  /** The event a press says, and the part of the page it is on: a game's page or the landing page's shelf. */
  event: Extract<EventName, "listing.followed" | "landing.followed">;
  at?: string;
}

const html = (markup: string): DocumentFragment => {
  const template = document.createElement("template");
  // (the markup is this repository's own writer's, src/core/offer.ts, which escapes what an author wrote)
  template.innerHTML = markup;
  return template.content;
};

const svgIcon = (name: Parameters<typeof icon>[0], size?: number): Node => html(icon(name, size)).firstChild!;

/** The address without anything a wish wrote into it. */
function forget(): void {
  const url = new URL(location.href);
  let had = false;
  for (const name of [...WISH, "checkout"]) {
    had ||= url.searchParams.has(name);
    url.searchParams.delete(name);
  }
  if (had) history.replaceState(history.state, "", url);
}

/** Writes a wish into the address, in place of any before it: a sign-in that leaves for Google or GitHub comes back to it. */
function remember(wish: Partial<Record<(typeof WISH)[number], string>>): void {
  const url = new URL(location.href);
  for (const name of WISH) url.searchParams.delete(name);
  for (const [name, value] of Object.entries(wish)) if (value !== undefined) url.searchParams.set(name, value);
  history.replaceState(history.state, "", url);
}

export class Gate {
  private asked: Promise<GateResponse> | null = null;
  private open: HTMLDialogElement | null = null;
  private readonly toastBox = h("p", { class: "gate-toast", role: "status" });
  /** The page was opened in a small window beside a game's play page (`window=1`), for one download. */
  private beside = false;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: GateOptions) {
    document.body.append(this.toastBox);
  }

  // ------------------------------------------------------------------ who

  /** Who is asking, read once and again after a sign-in or a payment. */
  who(fresh = false): Promise<GateResponse> {
    if (fresh || !this.asked) {
      this.asked = fetch("/api/gate", { credentials: "same-origin", cache: "no-store" }).then(async (response) => {
        if (!response.ok) throw new Error("gate");
        return (await response.json()) as GateResponse;
      });
      this.asked.then((who) => {
        this.greet(who);
        this.learnt(who);
      }, () => (this.asked = null));
    }
    return this.asked;
  }

  /**
   * What the event log is told once the page knows who is asking: a sign-in
   * that left the page for Google or GitHub has ended, with an account or
   * without one.
   */
  private learnt(who: GateResponse): void {
    const left = kept(SIGNIN_LEFT);
    if (left) this.finished(who.account ? (left as "download" | "remix" | "paywall") : null);
  }

  /** A sign-in asked for here ended: with an account, for `why`, or with none. */
  private finished(why: "download" | "remix" | "paywall" | null): void {
    keep(SIGNIN_LEFT, null);
    if (why) track("signin.finished", { props: { why } });
  }

  /** The page's bar: with an account signed in, its Sign in link is the account's handle and leads to its room. */
  private greet(who: GateResponse): void {
    const link = document.querySelector<HTMLAnchorElement>(".bar a.go");
    if (!link || !who.account) return;
    link.textContent = who.account;
    link.href = localized("/studio/");
  }

  /**
   * A wish, with the game it is for when this page is not that game's own:
   * the landing page's address then says which game a sign-in that left
   * comes back to (src/gate/main.ts reads `game`).
   */
  private wish(facts: GameFacts | null, wish: Partial<Record<(typeof WISH)[number], string>>): void {
    const own = document.querySelector<HTMLElement>("main.game")?.dataset.slug;
    remember({ ...wish, ...(facts && facts.slug !== own ? { game: facts.slug } : {}) });
  }

  private say(to: string, facts: GameFacts): void {
    track(this.options.event, { app: facts.id, props: { to, ...(this.options.at ? { at: this.options.at } : {}) } });
  }

  /** One line at the foot of the page, for a few seconds: a download that began, a thing that could not be done. */
  toast(message: string, ...more: Array<Node | string>): void {
    this.toastBox.replaceChildren(message, ...more);
    this.toastBox.classList.add("on");
    if (this.toastTimer) clearTimeout(this.toastTimer);
    // (in the small window beside a game, the line stays: closing the window is what comes next)
    if (!this.beside) this.toastTimer = setTimeout(() => this.toastBox.classList.remove("on"), more.length > 0 ? 9000 : 5000);
  }

  // --------------------------------------------------------------- dialogs

  /** One dialog at a time, over the page: its title, what it holds, and the cross that closes it. */
  private dialog(title: string, body: Array<Node | string>, name: string, onClose?: () => void): HTMLDialogElement {
    this.open?.close();
    const box = document.createElement("dialog");
    box.className = `gate gate-${name}`;
    const close = h("button", { class: "gate-close", type: "button", "aria-label": t("get.close") }, svgIcon("close", 18));
    close.addEventListener("click", () => box.close());
    const heading = h("h2", { text: title });
    heading.id = `gate-${name}-title`;
    box.setAttribute("aria-labelledby", heading.id);
    box.append(h("div", { class: "gate-sheet" }, close, heading, ...body));
    // A press on the backdrop, outside the sheet, closes it.
    box.addEventListener("click", (event) => {
      if (event.target === box) box.close();
    });
    box.addEventListener("close", () => {
      box.remove();
      if (this.open === box) this.open = null;
      onClose?.();
    });
    document.body.append(box);
    box.showModal();
    this.open = box;
    return box;
  }

  /**
   * An account, made or opened here. Resolves true once there is one, false
   * when the visitor closed the card. A sign-in with Google or GitHub leaves
   * the page: the wish in the address brings the visitor back to it.
   */
  private async account(lead: string, why: "download" | "remix" | "paywall"): Promise<boolean> {
    // (`remix` is the link code of the remix dialog: the prompt itself asks for no account)
    let who: GateResponse;
    try {
      who = await this.who();
    } catch {
      this.toast(t("signin.unavailable"));
      return false;
    }
    if (who.account) return true;
    return new Promise<boolean>((resolve) => {
      let done = false;
      const card = createSignIn({
        providers: who.providers,
        title: t("signin.title"),
        lead,
        why,
        onDone: () => {
          done = true;
          this.finished(why);
          void this.who(true).then(() => {
            box.close();
            resolve(true);
          }, () => resolve(false));
        },
      });
      // Google and GitHub leave the page: the tab keeps what the sign-in was for, and the page that comes back says how it ended.
      card.addEventListener("click", (event) => {
        if (event.target instanceof Element && event.target.closest("button.provider")) keep(SIGNIN_LEFT, why);
      }, true);
      // The card's own title is the dialog's.
      card.querySelector("h1")?.remove();
      const box = this.dialog(t("signin.title"), [card], "signin", () => {
        if (!done) resolve(false);
      });
      card.querySelector<HTMLElement>("input, button")?.focus();
    });
  }

  // -------------------------------------------------------------- download

  /** The download panel in a dialog: the landing page's shelf opens it. A game's page has the panel in the page. */
  download(facts: GameFacts): void {
    const body = h("div", { class: "gate-get" });
    body.append(html(downloadHtml(facts, lang())));
    const box = this.dialog(t("get.title", { title: facts.title }), [body], "download");
    this.bind(box, facts);
  }

  /** Takes over the tiles and the way to every version inside `root`, for `facts`' game. */
  bind(root: ParentNode, facts: GameFacts): void {
    for (const link of root.querySelectorAll<HTMLAnchorElement>("a[data-get]")) {
      link.addEventListener("click", (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        void this.get(facts, link.dataset.get as Wanted, link.dataset.version, link.dataset.device);
      });
    }
    for (const link of root.querySelectorAll<HTMLAnchorElement>('a[data-act="versions"]')) {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        void this.versions(facts);
      });
    }
    for (const link of root.querySelectorAll<HTMLAnchorElement>("a[data-source]")) link.addEventListener("click", () => this.say("source", facts));
  }

  /**
   * Hands over a device's package, a past version of it, or the source the
   * Studio holds. A visitor without an account signs in first, here. The
   * file is asked about before the browser is sent to it, so a refusal is
   * said on this page.
   */
  async get(facts: GameFacts, wanted: Wanted, version?: string, device?: string, pressed = true): Promise<void> {
    const name = device ?? facts.packages.find((stored) => stored.target === wanted)?.name ?? "";
    // A wish the address brought (from a play page, or back from a sign-in) was pressed and said where it was pressed.
    if (pressed) this.say(wanted === "source" ? "source" : "package", facts);
    this.wish(facts, { get: wanted, ...(version ? { version } : {}) });
    const lead = wanted === "source" ? t("get.signin.source", { title: facts.title }) : t("get.signin", { title: facts.title, device: name });
    if (!(await this.account(lead, "download"))) return forget();
    forget();
    const url = wanted === "source"
      ? `/api/apps/${encodeURIComponent(facts.id)}/source`
      : `/api/apps/${encodeURIComponent(facts.id)}/native/${wanted}${version ? `?version=${encodeURIComponent(version)}` : ""}`;
    let response: Response;
    try {
      response = await fetch(url, { method: "HEAD", credentials: "same-origin", cache: "no-store" });
    } catch {
      return this.toast(t("toast.downloadUnavailable"));
    }
    if (response.status === 401) {
      // The session ended between the two questions: ask again, once.
      await this.who(true).catch(() => null);
      return this.toast(t("toast.downloadUnavailable"));
    }
    if (response.status === 402) return void this.paywall(facts, "versions");
    if (!response.ok) return this.toast(t("toast.downloadUnavailable"));
    // The answer is an attachment: the browser keeps this page and saves the file.
    location.assign(url);
    // The visit reached a package: said here, where the download starts, for nobody (the server says whose download it was, and nothing of the visit).
    if (wanted !== "source") track("visit.step", { app: facts.id, props: { step: "package" } });
    const again = h("a", { href: url, text: t("get.again") });
    // Opened beside a game's play page for this one download: the way back is to close.
    const back = this.beside ? h("button", { class: "gate-back", type: "button", onclick: () => window.close() }, t("get.back")) : null;
    this.toast(wanted === "source" ? t("get.started.source", { title: facts.title }) : t("get.started", { title: facts.title, device: name }), " ", again, ...(back ? [" ", back] : []));
  }

  // -------------------------------------------------------------- versions

  /** Every version of the game's packages. A past one is a member's: anyone else sees the list and the way to the membership. */
  async versions(facts: GameFacts, pressed = true): Promise<void> {
    if (pressed) this.say("versions", facts);
    const who = await this.who().catch(() => null);
    const member = who?.member === true;
    const body = h("div", { class: "gate-versions" });
    if (!member && who?.membership.open !== false) {
      body.append(h("p", { class: "gate-banner" },
        svgIcon("lock", 20), h("span", { text: t("versions.member") }),
        h("button", { class: "btn small", type: "button", onclick: () => void this.paywall(facts, "versions") }, t("account.join"))));
    }
    body.append(html(versionsHtml(facts, lang(), member)));
    const box = this.dialog(t("versions.title", { title: facts.title }), [body], "versions");
    this.bind(box, facts);
  }

  // --------------------------------------------------------------- paywall

  /**
   * The membership: what it gives, the two ways to pay, and the way on to
   * payment. A visitor signs in first; the plan is in the address for the
   * trip. The payment returns to this page (`back`), where `resume` waits
   * for the membership and opens what was asked for.
   */
  async paywall(facts: GameFacts | null, reason: "versions" | "member", chosen: Plan = "year"): Promise<void> {
    const who = await this.who().catch(() => null);
    if (!who || !who.membership.open) return this.toast(t("paywall.closed"));
    if (who.member) return facts ? this.versions(facts, false) : undefined;
    const { price, annualPrice, currency } = who.membership;
    const money = (amount: number) => formatPrice(lang(), amount, currency);
    let plan: Plan = chosen;
    const option = (id: Plan, title: string, detail: string) => {
      const input = h("input", { type: "radio", name: "plan", id: `plan-${id}` });
      input.checked = id === plan;
      input.addEventListener("change", () => (plan = id));
      return h("label", { class: "plan" }, input, h("span", { class: "plan-text" }, h("b", { text: title }), h("small", { text: detail })));
    };
    const note = h("p", { class: "signin-note", role: "alert" });
    const go = h("button", { class: "btn", type: "button" }, t("paywall.go"));
    let timeZone: string | undefined;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      // (a browser that cannot say gets the checkout its network address decides)
    }
    // The same question the room asks: a buyer in mainland China whom neither the address nor the zone shows.
    const askMainland = LEGAL.merchantOfRecord && !mainlandZone(timeZone) && (navigator.languages ?? [navigator.language]).some((tag) => /^zh\b/i.test(tag ?? ""));
    const title = t(reason === "versions" ? "paywall.versions" : "account.join");

    const pay = async (mainland = false) => {
      go.disabled = true;
      note.textContent = "";
      // A refusal is said under the button while the plans are still up, and at the foot of the page once a sign-in took their place.
      const refuse = () => (this.open?.contains(go) ? void (note.textContent = t("paywall.failed")) : this.toast(t("paywall.failed")));
      try {
        // The address keeps the plan, so a sign-in that leaves for Google or GitHub comes back to this choice.
        this.wish(facts, { join: plan, ...(facts && reason === "versions" ? { versions: "" } : {}) });
        if (!(await this.account(t("paywall.signIn"), "paywall"))) return forget();
        forget();
        // (an account that signed in just now may be a member already)
        if ((await this.who()).member) return facts ? void this.versions(facts) : this.open?.close();
        track("paywall.plan", { props: { plan, reason } });
        const response = await fetch("/api/account/checkout", {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json", "x-pocket-lang": lang() },
          // (the payment returns to the game's page, from the landing page too: that page waits for the membership)
          body: JSON.stringify({ plan, back: facts ? new URL(facts.page).pathname : location.pathname, ...(timeZone ? { timeZone } : {}), ...(mainland ? { mainland: true } : {}) }),
        });
        const reply = (await response.json().catch(() => null)) as { url?: string } | null;
        if (!response.ok || !reply?.url) return refuse();
        if (facts && reason === "versions") sessionStorage.setItem(AFTER_PAYMENT, "versions");
        location.assign(reply.url);
      } catch {
        refuse();
      } finally {
        go.disabled = false;
      }
    };
    go.addEventListener("click", () => void pay());

    const perks = h("ul", { class: "perks" }, ...PERKS.map((entry) => {
      const picture = h("span", { class: "perk-icon", "aria-hidden": "true" });
      picture.append(html(perkSvg(entry.id)));
      return h("li", {}, picture, h("span", {}, perkText(entry.id, lang()), entry.soon ? h("small", { text: soonText(lang()) }) : null));
    }));
    const card = h("div", { class: "gate-paywall" },
      perks,
      h("div", { class: "plans", role: "radiogroup", "aria-label": t("paywall.plan") },
        option("year", t("paywall.perMonth", { price: money(Math.round(annualPrice / 12)) }), t("paywall.yearly", { price: money(annualPrice) })),
        option("month", t("paywall.perMonth", { price: money(price) }), t("paywall.monthly"))),
      h("p", { class: "paywall-terms" }, ...rich("paywall.terms", {
        terms: h("a", { href: localized(TERMS_PATH), target: "_blank", rel: "noopener" }, t("legal.terms")),
        privacy: h("a", { href: localized(PRIVACY_PATH), target: "_blank", rel: "noopener" }, t("legal.privacy")),
      })),
      go,
      note,
      askMainland ? h("button", { class: "quiet", type: "button", onclick: () => void pay(true) }, t("paywall.mainland")) : null,
      h("button", { class: "quiet", type: "button", onclick: () => this.open?.close() }, t("paywall.notNow")));
    this.dialog(title, [card], "paywall");
    go.focus();
    track("paywall.shown", { props: { reason } });
  }

  // ----------------------------------------------------------------- remix

  /**
   * The remix dialog. Its first part is one prompt for the visitor's coding
   * agent, whichever that is: the agent copies the game to the visitor's
   * computer and changes it there. It asks for no account and for nothing of
   * the Studio's on that computer. Its second part is for the day the remix
   * is published: a link code, which is an account's, and the guide. `link`
   * opens the dialog with the code asked for (a sign-in that left the page
   * and came back).
   */
  async remix(facts: GameFacts, link = false): Promise<void> {
    forget();
    // (an address may ask for the dialog of a game that has no Remix: there is nothing to copy)
    if (!facts.remixable) return;
    const origin = new URL(facts.page).origin;
    const repository = facts.source?.kind === "repository" ? facts.source.url : null;
    const elsewhere = facts.kind === "site" && repository !== null;
    const text = remixPrompt({ kind: facts.kind, id: facts.id, title: facts.title, page: facts.page, repository }, origin, lang());

    const copyButton = (said: string, event: "remix.copied" | "agent.copied") => {
      const button = h("button", { class: "gate-copy", type: "button" }, t("room.copy"));
      button.addEventListener("click", async () => {
        try {
          await navigator.clipboard.writeText(said);
        } catch {
          return;
        }
        button.textContent = t("room.copied");
        // The prompt taken away is the step the remix funnel counts; the code's copy is a setup card's.
        if (event === "remix.copied") track(event, { app: facts.id });
        else track(event);
        setTimeout(() => (button.textContent = t("room.copy")), 1600);
      });
      return button;
    };
    const copy = copyButton(text, "remix.copied");

    // The link code: an account's, made when the visitor asks for one.
    const code = h("div", { class: "gate-linking" });
    const ask = h("button", { class: "btn small ghost", type: "button" }, t("remix.code"));
    code.append(ask);
    let making = false;
    const make = async () => {
      // (a second press while the first is on its way does nothing)
      if (making) return;
      making = true;
      // A sign-in takes this dialog's place, and one with Google or GitHub leaves the page: the address says what to open again.
      this.wish(facts, { remix: "", link: "" });
      const here = this.open;
      const signedIn = await this.account(t("remix.signin"), "remix");
      forget();
      // The dialog is gone when a sign-in card stood in its place: it opens again, with the code when there is an account now.
      if (this.open !== here || !here?.isConnected) return void this.remix(facts, signedIn);
      if (!signedIn) return void (making = false);
      ask.disabled = true;
      ask.textContent = t("agent.linking");
      try {
        const response = await fetch("/api/links", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: "{}" });
        if (!response.ok) throw new Error("link");
        const made = ((await response.json()) as CreateLinkResponse).code;
        code.replaceChildren(
          h("div", { class: "gate-block" }, h("code", { class: "gate-code", text: made }), copyButton(made, "agent.copied")),
          h("p", { class: "gate-say", text: t("remix.code.note") }));
        // (the dialog may be taller than the window: the code is what was asked for)
        code.scrollIntoView({ block: "nearest" });
        track("remix.code", { app: facts.id });
      } catch {
        making = false;
        ask.disabled = false;
        ask.textContent = t("remix.code");
        this.toast(t("agent.linkFailed"));
      }
    };
    ask.addEventListener("click", () => void make());

    // The source the remix starts from, as the download panel shows it.
    const source = h("div", { class: "gate-source" });
    source.append(html(sourceHtml(facts, lang())));
    // How a remix is published, and for a game built on Pocket3D, the engine's own source and documents.
    const links = h("p", { class: "gate-links" },
      h("a", { href: `${localized(REMIX_GUIDE)}#${elsewhere ? "publish" : "pocket-app"}`, target: "_blank", rel: "noopener" }, t(elsewhere ? "remix.guide" : "remix.guide.pocket")),
      facts.kind === "site" ? h("a", { href: POCKET3D_SOURCE, target: "_blank", rel: "noopener" }, t("remix.engine")) : null);
    const body = h("div", { class: "gate-remix" },
      h("p", { class: "gate-lead", text: t("remix.page.lead") }),
      h("div", { class: "gate-block gate-prompt" }, h("code", { class: "gate-code", text }), copy),
      h("p", { class: "gate-say", text: t("remix.any") }),
      source,
      h("section", { class: "gate-publish" },
        // (a Pocket app's code is asked for when the game is copied, before anything is published)
        h("h3", { text: t(elsewhere ? "remix.publish" : "remix.code.title") }),
        h("p", { class: "gate-say", text: t(elsewhere ? "remix.publish.text" : "remix.publish.pocket") }),
        code,
        links));
    const box = this.dialog(t("remix.title", { title: facts.title }), [body], "remix");
    this.bind(box, facts);
    copy.focus();
    // (opened again for the code, after a sign-in, it is the same prompt shown to the same visitor: said once)
    if (!link) track("remix.prompted", { app: facts.id });
    // Back from a sign-in that was for the code: it is made now. Without an account the dialog waits for the press.
    if (link && (await this.who().catch(() => null))?.account) void make();
  }

  // ---------------------------------------------------------------- resume

  /**
   * What the address asks for, on a game's page: a package (`get`, from the
   * play page or from a sign-in that left), the remix dialog (`remix`, and
   * with `link` its link code), every version (`versions`), the plans
   * (`join`), or a payment that came back (`checkout`).
   */
  resume(facts: GameFacts): void {
    const query = new URL(location.href).searchParams;
    this.beside = query.has("window");
    const paid = query.get("checkout");
    if (paid) {
      forget();
      if (paid === "cancelled") return this.toast(t("checkout.cancelled"));
      // The payment provider tells the server, not this page: look until the membership shows, for half a minute.
      this.toast(t("checkout.success"));
      const after = sessionStorage.getItem(AFTER_PAYMENT);
      sessionStorage.removeItem(AFTER_PAYMENT);
      let tries = 0;
      const look = () => void this.who(true).then((who) => {
        if (who.member) {
          this.toast(t("checkout.member.page"));
          if (after === "versions") void this.versions(facts, false);
        } else if (++tries <= 15) setTimeout(look, 2000);
      }, () => null);
      return look();
    }
    const join = query.get("join");
    if (join === "month" || join === "year") return void this.paywall(query.has("versions") ? facts : null, query.has("versions") ? "versions" : "member", join);
    const get = query.get("get");
    if (get !== null) {
      const version = query.get("version") ?? undefined;
      if (get === "source" || (NATIVE_TARGETS as readonly string[]).includes(get)) return void this.get(facts, get as Wanted, version, undefined, false);
      // (`?get` with no device: the play page's Download, for a visitor it could not show the panel)
      forget();
      return document.getElementById("download")?.scrollIntoView({ block: "start" });
    }
    if (query.has("remix")) return void this.remix(facts, query.has("link"));
    if (query.has("versions")) {
      forget();
      return void this.versions(facts, false);
    }
  }
}

/** A game's facts in the page's language, from the Studio. `origin` is the Studio's, for a page on another host. */
export async function readFacts(slug: string, language: string, origin = ""): Promise<GameFacts> {
  const response = await fetch(`${origin}/api/games/${encodeURIComponent(slug)}${language === "en" ? "" : `?lang=${language}`}`, { credentials: "omit" });
  if (!response.ok) throw new Error("facts");
  return (await response.json()) as GameFacts;
}
